/**
 * 指标卡片组件
 * 展示单个关键指标，支持趋势显示和异常高亮
 */
import React from 'react';
import { Card, Statistic, Space, Tag } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import type { MetricData } from '@/types/dashboard';
import './style.css';

interface MetricCardProps {
  data: MetricData;
  loading?: boolean;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  data,
  loading = false,
  onClick
}) => {
  const { title, value, unit, trend, status } = data;

  // 根据状态确定卡片样式
  const getCardClassName = () => {
    const baseClass = 'metric-card';
    const statusClass = status === 'error' ? 'metric-card-error' :
                        status === 'warning' ? 'metric-card-warning' :
                        'metric-card-normal';
    const clickableClass = onClick ? 'metric-card-clickable' : '';

    return `${baseClass} ${statusClass} ${clickableClass}`.trim();
  };

  // 数值颜色 - 使用 CSS 变量，自动跟随主题
  const getValueColor = () => {
    return 'var(--color-text-primary)';
  };

  // 趋势颜色 - 使用 CSS 变量，自动跟随主题
  const getTrendColor = () => {
    if (!trend) return undefined;

    if (status === 'error') return 'var(--color-error)';
    if (status === 'warning') return 'var(--color-warning)';
    return trend.direction === 'up' ? 'var(--color-success)' : 'var(--color-error)';
  };

  // 趋势图标
  const TrendIcon = trend?.direction === 'up' ? ArrowUpOutlined : ArrowDownOutlined;

  // 状态标签
  const renderStatusTag = () => {
    if (status === 'normal') return null;

    return (
      <Tag
        color={status === 'error' ? 'error' : 'warning'}
        icon={<InfoCircleOutlined />}
      >
        {status === 'error' ? '异常' : '注意'}
      </Tag>
    );
  };

  return (
    <Card
      className={getCardClassName()}
      loading={loading}
      hoverable={!!onClick}
      onClick={onClick}
      variant="borderless"
    >
      <div className="metric-card-header">
        <span className="metric-card-title">{title}</span>
        {renderStatusTag()}
      </div>

      <div className="metric-card-body">
        <Statistic
          value={value}
          suffix={unit}
          valueStyle={{
            fontSize: 'var(--font-size-data-large)',
            fontWeight: 700,
            fontFamily: 'var(--font-family-number)',
            color: getValueColor()
          }}
        />
      </div>

      {trend && (
        <div className="metric-card-footer">
          <Space size={4}>
            <TrendIcon style={{ color: getTrendColor() }} />
            <span style={{ color: getTrendColor() }}>
              {trend.value.toFixed(1)}%
            </span>
            <span className="metric-card-period">{trend.period}</span>
          </Space>
        </div>
      )}
    </Card>
  );
};

export default MetricCard;
